import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import { StudentViewProvider } from './context/StudentViewContext';
import { ThemeProvider } from './context/ThemeContext';
import { DocumentPreviewProvider } from './context/DocumentPreviewContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import Layout from './components/Layout';

// Landing page
import LandingPage from './pages/public/landing/LandingPage';
import ProductPage from './pages/public/landing/ProductPage';

// Public Pages
import LoginPage from './pages/public/LoginPage';
import RegisterPage from './pages/public/RegisterPage';

// Student Pages
import UserDashboard from './pages/student/UserDashboard';
import TakeAssignment from './pages/student/TakeAssignment';

// Instructor Pages
import CreateAssignment from './pages/instructor/CreateAssignment';
import ViewSubmissions from './pages/instructor/ViewSubmissions';
import GradeSubmission from './pages/instructor/GradeSubmission';

// Course Pages
import CourseDetail from './pages/course/CourseDetail';
import CreateCourse from './pages/course/CreateCourse';
import PageView from './pages/course/PageView';
import PageEditor from './pages/course/PageEditor';
import DiscussionThread from './pages/course/DiscussionThread';

// Shared Pages
import AccountPage from './pages/shared/AccountPage';
import CalendarPage from './pages/shared/CalendarPage';
import NotificationsPage from './pages/shared/NotificationsPage';
import CoursesPage from './pages/shared/CoursesPage';
import SearchResultsPage from './pages/shared/SearchResultsPage';
import LearningPathList from './pages/shared/LearningPathList';
import LearningPathPage from './pages/shared/LearningPathPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import CourseManagement from './pages/admin/CourseManagement';
import AdminCourseDetail from './pages/admin/AdminCourseDetail';
import UserManagement from './pages/admin/UserManagement';
import DepartmentManagement from './pages/admin/DepartmentManagement';
import DepartmentDetail from './pages/admin/DepartmentDetail';
import TermManagement from './pages/admin/TermManagement';
import AISettings from './pages/admin/AISettings';
import RoleManagement from './pages/admin/RoleManagement';

const isLandingMode = process.env.REACT_APP_MODE === 'landing';

function CourseDetailRedirect() {
  const { courseId } = useParams();
  return <Navigate to={`/courses/${courseId}/home`} replace />;
}

function RouteAnnouncer() {
  const location = useLocation();
  const title = document.title || 'Page';

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      role="status"
      key={location.pathname}
      style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }}
    >
      {`Navigated to ${title}`}
    </div>
  );
}

function App() {
  if (isLandingMode) {
    return (
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
    <AuthProvider>
    <StudentViewProvider>
      <SidebarProvider>
        <Router>
          <DocumentPreviewProvider>
          <div className="App">
            <RouteAnnouncer />
            <Navbar />
            <Layout>
              <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* User Dashboard */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <UserDashboard />
                </PrivateRoute>
              }
            />

            {/* Account Page */}
            <Route
              path="/account"
              element={
                <PrivateRoute>
                  <AccountPage />
                </PrivateRoute>
              }
            />

            {/* Calendar Page */}
            <Route
              path="/calendar"
              element={
                <PrivateRoute>
                  <CalendarPage />
                </PrivateRoute>
              }
            />

            {/* Notifications Page */}
            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <NotificationsPage />
                </PrivateRoute>
              }
            />

            {/* Courses Page (shared, all roles) */}
            <Route
              path="/courses"
              element={
                <PrivateRoute>
                  <CoursesPage />
                </PrivateRoute>
              }
            />

            {/* Search */}
            <Route
              path="/search"
              element={
                <PrivateRoute>
                  <SearchResultsPage />
                </PrivateRoute>
              }
            />

            {/* Learning Paths */}
            <Route
              path="/learning-paths"
              element={
                <PrivateRoute>
                  <LearningPathList />
                </PrivateRoute>
              }
            />
            <Route
              path="/learning-paths/:id"
              element={
                <PrivateRoute>
                  <LearningPathPage />
                </PrivateRoute>
              }
            />

            {/* Course Routes */}
            <Route
              path="/courses/:courseId"
              element={
                <PrivateRoute>
                  <CourseDetailRedirect />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/discussions/:topicId"
              element={
                <PrivateRoute>
                  <DiscussionThread />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/:section"
              element={
                <PrivateRoute>
                  <CourseDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/assignments/:assignmentId"
              element={
                <PrivateRoute>
                  <TakeAssignment />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/assignments/:assignmentId/edit"
              element={
                <PrivateRoute>
                  <CreateAssignment />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/assignments/create"
              element={
                <PrivateRoute>
                  <CreateAssignment />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/assignments/:assignmentId/submissions"
              element={
                <PrivateRoute>
                  <ViewSubmissions />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/assignments/:assignmentId/submissions/:submissionId"
              element={
                <PrivateRoute>
                  <GradeSubmission />
                </PrivateRoute>
              }
            />

            {/* Page Routes */}
            <Route
              path="/courses/:courseId/pages/create"
              element={
                <PrivateRoute>
                  <PageEditor />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/pages/:pageId/edit"
              element={
                <PrivateRoute>
                  <PageEditor />
                </PrivateRoute>
              }
            />
            <Route
              path="/courses/:courseId/pages/:pageId"
              element={
                <PrivateRoute>
                  <PageView />
                </PrivateRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <PrivateRoute requireAdmin>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/departments"
              element={
                <PrivateRoute requireAdmin>
                  <DepartmentManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/departments/:departmentId"
              element={
                <PrivateRoute requireAdmin>
                  <DepartmentDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/terms"
              element={
                <PrivateRoute requireAdmin>
                  <TermManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/courses"
              element={
                <PrivateRoute requireAdmin>
                  <CourseManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/courses/create"
              element={
                <PrivateRoute requireAdmin>
                  <CreateCourse />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/courses/:courseId"
              element={
                <PrivateRoute requireAdmin>
                  <AdminCourseDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <PrivateRoute requireAdmin>
                  <UserManagement />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/roles"
              element={
                <PrivateRoute requireAdmin>
                  <RoleManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/ai-settings"
              element={
                <PrivateRoute requireAdmin>
                  <AISettings />
                </PrivateRoute>
              }
            />

            {/* Legacy redirects - remove old student/teacher routes */}
            <Route path="/student/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/teacher/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/student/assignments/:id" element={<Navigate to="/dashboard" replace />} />
            <Route path="/teacher/courses/create" element={<Navigate to="/admin/courses/create" replace />} />
            <Route path="/teacher/assignments/create" element={<Navigate to="/dashboard" replace />} />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
            </Layout>
          </div>
          </DocumentPreviewProvider>
        </Router>
      </SidebarProvider>
    </StudentViewProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
